import { useEffect, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  type TooltipProps,
} from "recharts";
import type { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import { DefinitionTooltip, type DefinitionTooltipInfo } from "src/components/common";
import type {
  PaidMediaOverviewScaleBenchmarks,
  PaidMediaOverviewScalePoint,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewCurrency,
  formatOverviewMultiplier,
  formatOverviewText,
} from "../../utils/PaidMediaOverviewFormat";
import { useOverviewAttribution } from "../../utils/PaidMediaOverviewAttributionContext";
import { overviewDataBasis } from "../../utils/PaidMediaOverviewTooltips";
import {
  overviewPlatformLabel,
  overviewQuadrantReason,
  overviewStatusLabel,
} from "../../utils/PaidMediaOverviewLabels";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewSegmented,
} from "./OverviewPrimitives";
import {
  OVERVIEW_AXIS_LABEL_COLOR,
  OVERVIEW_GRID_COLOR,
  OVERVIEW_PLATFORM_COLOR,
  OVERVIEW_REFERENCE_LABEL_COLOR,
  OVERVIEW_REFERENCE_LINE_COLOR,
  OVERVIEW_UNKNOWN_PLATFORM_COLOR,
  overviewAxisProps,
} from "./OverviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/PaidMediaChartRtl";

type QuadrantView = "platform" | "campaign";

const QUADRANT_BUBBLE_RANGE: [number, number] = [60, 700];

interface QuadrantChartPoint extends PaidMediaOverviewScalePoint {
  fill: string;
}

interface QuadrantCardProps {
  platformView: PaidMediaOverviewScalePoint[];
  campaignView: PaidMediaOverviewScalePoint[];
  benchmarks: PaidMediaOverviewScaleBenchmarks | null;
  currency: string | null;
  loading: boolean;
  /** Opens the campaign drawer. Campaign View only — platform bubbles aren't campaigns. */
  onCampaignSelect?: (_point: PaidMediaOverviewScalePoint) => void;
}

const QuadrantCard: FC<QuadrantCardProps> = ({
  platformView,
  campaignView,
  benchmarks,
  currency,
  loading,
  onCampaignSelect,
}) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();
  const [view, setView] = useState<QuadrantView>("platform");
  const mode = useOverviewAttribution();

  const campaignViewDisabled = mode === "shopify";
  useEffect(() => {
    if (campaignViewDisabled && view === "campaign") {
      setView("platform");
    }
  }, [campaignViewDisabled, view]);

  const points = view === "platform" ? platformView : campaignView;

  const plottedPoints: QuadrantChartPoint[] = useMemo(
    () =>
      (points ?? [])
        .filter(point => point?.roas !== null && Number.isFinite(point?.roas))
        .map(point => ({
          ...point,
          // Falling back to Meta's colour would silently repaint an unrecognised
          // platform as Meta — the whole chart then reads as Meta-only. Use a
          // neutral colour so an unexpected `platform` value is visible instead.
          fill: OVERVIEW_PLATFORM_COLOR[point.platform] ?? OVERVIEW_UNKNOWN_PLATFORM_COLOR,
        })),
    [points]
  );

  const isCampaignView = view === "campaign";
  const canSelectCampaign = isCampaignView && Boolean(onCampaignSelect);

  // Recharts hands Scatter's onClick the rendered symbol node, which carries the
  // original datum on `payload` but also spreads its fields onto the node itself
  // depending on the shape in use — read `payload` first, fall back to the node.
  const handlePointClick = (node: unknown) => {
    if (!canSelectCampaign) {
      return;
    }
    const candidate = node as { payload?: QuadrantChartPoint } & Partial<QuadrantChartPoint>;
    const point = candidate?.payload ?? (candidate as QuadrantChartPoint | undefined);
    if (!point?.campaign_id) {
      return;
    }
    onCampaignSelect?.(point);
  };

  const maxRevenue = useMemo(
    () => plottedPoints.reduce((max, point) => Math.max(max, point.revenue ?? 0), 0),
    [plottedPoints]
  );

  const spendBenchmark = benchmarks?.spend_benchmark ?? null;
  const roasBenchmark = benchmarks?.roas_benchmark ?? null;

  const chartHelpInfo: DefinitionTooltipInfo = {
    description: t("paidMediaOverview.quadrant.helpDescription"),
    formula: t("paidMediaOverview.quadrant.helpFormula"),
    bullets: [
      t("paidMediaOverview.quadrant.helpBulletGrowthOpportunity"),
      t("paidMediaOverview.quadrant.helpBulletScaleWinner"),
      t("paidMediaOverview.quadrant.helpBulletLowPriority"),
      t("paidMediaOverview.quadrant.helpBulletHighSpendLowReturn"),
      t("paidMediaOverview.quadrant.helpBulletBenchmarks", {
        spend: formatOverviewCurrency(spendBenchmark, currency),
        roas: formatOverviewMultiplier(roasBenchmark),
      }),
    ],
    dataBasis: overviewDataBasis(t, mode),
  };

  const renderTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
    if (!active || !payload?.length) {
      return null;
    }
    const point = payload[0]?.payload as QuadrantChartPoint | undefined;
    if (!point) {
      return null;
    }

    const platformLabel = overviewPlatformLabel(t, point.platform);
    const classification = overviewStatusLabel(t, point.quadrant);
    const reasonSubject = view === "campaign" ? point.label : platformLabel;

    return (
      <div className="paid-media-overview__quadrant-tooltip">
        <div className="paid-media-overview__quadrant-tooltip-title">{platformLabel}</div>
        {view === "campaign" ? (
          <div className="paid-media-overview__quadrant-tooltip-title">{point.label}</div>
        ) : null}
        <div className="paid-media-overview__quadrant-tooltip-body">
          <div>
            {t("paidMediaOverview.metrics.spend")}: {formatOverviewText(point.formatted_spend)}
          </div>
          <div>
            {t("paidMediaOverview.metrics.revenue")}: {formatOverviewText(point.formatted_revenue)}
          </div>
          <div>
            {t("paidMediaOverview.metrics.roas")}:{" "}
            {point.formatted_roas ?? formatOverviewMultiplier(point.roas)}
          </div>
        </div>
        {classification ? (
          <div className="paid-media-overview__quadrant-tooltip-classification">
            {t("paidMediaOverview.quadrant.tooltipClassification", { value: classification })}
          </div>
        ) : null}
        {point.quadrant ? (
          <div className="paid-media-overview__quadrant-tooltip-reason">
            {t("paidMediaOverview.quadrant.tooltipReason", {
              value: overviewQuadrantReason(t, point.quadrant, reasonSubject),
            })}
          </div>
        ) : null}
        {canSelectCampaign && point.campaign_id ? (
          <div className="paid-media-overview__quadrant-tooltip-hint">
            {t("paidMediaOverview.quadrant.tooltipClickHint")}
          </div>
        ) : null}
      </div>
    );
  };

  const renderChart = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!plottedPoints.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <>
        <div className="paid-media-overview__quadrant-labels" aria-hidden>
          <span className="paid-media-overview__quadrant-label paid-media-overview__quadrant-label--tl">
            {t("paidMediaOverview.statuses.growthOpportunity")}
          </span>
          <span className="paid-media-overview__quadrant-label paid-media-overview__quadrant-label--tr">
            {t("paidMediaOverview.statuses.scaleWinner")}
          </span>
          <span className="paid-media-overview__quadrant-label paid-media-overview__quadrant-label--bl">
            {t("paidMediaOverview.statuses.lowPriority")}
          </span>
          <span className="paid-media-overview__quadrant-label paid-media-overview__quadrant-label--br">
            {t("paidMediaOverview.statuses.highSpendLowReturn")}
          </span>
        </div>
        <ResponsiveContainer>
          <ScatterChart margin={chartRtl.margin({ top: 20, right: 20, bottom: 20, left: 10 })}>
            <CartesianGrid stroke={OVERVIEW_GRID_COLOR} />
            <XAxis
              type="number"
              dataKey="spend"
              name={t("paidMediaOverview.metrics.spend")}
              tickFormatter={value => formatOverviewCurrency(value, currency)}
              reversed={chartRtl.axisReversed}
              {...overviewAxisProps}
              label={{
                value: t("paidMediaOverview.quadrant.axisSpend"),
                fontSize: 10,
                fill: OVERVIEW_AXIS_LABEL_COLOR,
                position: "insideBottom",
                offset: -5,
              }}
            />
            <YAxis
              type="number"
              dataKey="roas"
              name={t("paidMediaOverview.metrics.roas")}
              tickFormatter={value => `${value}x`}
              orientation={chartRtl.yAxisOrientation}
              {...overviewAxisProps}
              label={{
                value: t("paidMediaOverview.quadrant.axisRoas"),
                angle: -90,
                fontSize: 10,
                fill: OVERVIEW_AXIS_LABEL_COLOR,
                position: chartRtl.isRTL ? "insideRight" : "insideLeft",
              }}
            />

            <ZAxis
              type="number"
              dataKey="revenue"
              domain={[0, maxRevenue || 1]}
              range={QUADRANT_BUBBLE_RANGE}
            />
            {spendBenchmark !== null && Number.isFinite(spendBenchmark) ? (
              <ReferenceLine
                x={spendBenchmark}
                stroke={OVERVIEW_REFERENCE_LINE_COLOR}
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{
                  value: t("paidMediaOverview.quadrant.spendBenchmark"),
                  position: "top",
                  fontSize: 9,
                  fill: OVERVIEW_REFERENCE_LABEL_COLOR,
                }}
              />
            ) : null}
            {roasBenchmark !== null && Number.isFinite(roasBenchmark) ? (
              <ReferenceLine
                y={roasBenchmark}
                stroke={OVERVIEW_REFERENCE_LINE_COLOR}
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{
                  value: t("paidMediaOverview.quadrant.roasBenchmark"),
                  position: chartRtl.isRTL ? "insideTopLeft" : "insideTopRight",
                  fontSize: 9,
                  fill: OVERVIEW_REFERENCE_LABEL_COLOR,
                }}
              />
            ) : null}
            <Tooltip cursor={{ strokeDasharray: "3 3" }} content={renderTooltip} />
            <Scatter
              data={plottedPoints}
              fillOpacity={0.7}
              cursor={canSelectCampaign ? "pointer" : undefined}
              onClick={canSelectCampaign ? handlePointClick : undefined}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </>
    );
  };

  return (
    <article className="paid-media-panel">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>{t("paidMediaOverview.quadrant.title")}</h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.quadrant.subtitle")}
          </p>
        </div>
        <div className="paid-media-overview__panel-header-actions paid-media-overview__granularity-segmented">
          <OverviewSegmented
            value={view}
            onChange={setView}
            size="sm"
            ariaLabel={t("paidMediaOverview.quadrant.viewAria")}
            options={[
              { value: "platform", label: t("paidMediaOverview.quadrant.viewPlatform") },
              {
                value: "campaign",
                label: t("paidMediaOverview.quadrant.viewCampaign"),
                disabled: campaignViewDisabled,
                tooltip: campaignViewDisabled
                  ? { description: t("paidMediaOverview.quadrant.viewCampaignDisabledByShopify") }
                  : undefined,
              },
            ]}
          />
          <DefinitionTooltip
            label={t("paidMediaOverview.quadrant.helpLabel")}
            info={chartHelpInfo}
            align="end"
            formulaTitle={t("paidMediaOverview.quadrant.helpFormulaTitle")}
          />
        </div>
      </div>
      <div className="paid-media-overview__quadrant-chart">{renderChart()}</div>
    </article>
  );
};

export default QuadrantCard;
